import React, { useCallback, useState } from 'react';
import { useChannelActionContext, useChannelStateContext, type StreamMessage } from 'stream-chat-react';
import { ErrorLogger } from '../../utils/Logger';
import type { StreamChatType } from '../types';
import './QuotedMessageDisplay.scss';

interface QuotedMessageDisplayProps {
  quotedMessage: StreamMessage<StreamChatType> | any;
}

export const QuotedMessageDisplay: React.FC<QuotedMessageDisplayProps> = ({ quotedMessage }) => {
  const { channel } = useChannelStateContext<StreamChatType>();
  const { jumpToMessage } = useChannelActionContext<StreamChatType>();
  const [isLoading, setIsLoading] = useState(false);

  // Flash animation helper
  const flashMessage = useCallback((messageElement: HTMLElement) => {
    const originalBackground = messageElement.style.backgroundColor;
    let flashCount = 0;
    const maxFlashes = 3;
    
    const flashInterval = setInterval(() => {
      if (flashCount >= maxFlashes * 2) {
        clearInterval(flashInterval);
        messageElement.style.backgroundColor = originalBackground;
        messageElement.style.transform = '';
        return;
      }
      
      if (flashCount % 2 === 0) {
        messageElement.style.backgroundColor = 'rgba(137, 137, 137, 0.29)';
        messageElement.style.transform = 'scale(1.02)';
        messageElement.style.transition = 'all 0.15s ease';
      } else {
        messageElement.style.backgroundColor = originalBackground || 'white';
        messageElement.style.transform = 'scale(1)';
      }
      
      flashCount++;
    }, 250);
  }, []);

  // Scroll to the original quoted message when clicked
  const handleClick = useCallback(async () => {
    if (!quotedMessage || isLoading) return;
    const messageId = quotedMessage.id;
    
    // First, check if message is already in DOM
    const liElement = document.querySelector(`[data-message-id="${messageId}"]`) as HTMLElement;
    
    if (liElement) {
      // Message is already loaded - just scroll to it
      const messageElement = liElement.querySelector('.str-chat__message') as HTMLElement;
      
      if (messageElement) {
        liElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        
        setTimeout(() => {
          flashMessage(messageElement);
        }, 300);
      }
    } else {
      // Message not in DOM - need to load it from Stream Chat
      setIsLoading(true);
      
      try {
        if (jumpToMessage) {
          // Use Stream Chat's jumpToMessage if available
          await jumpToMessage(messageId);
          
          // Wait for the message to be rendered in the DOM
          setTimeout(() => {
            const loadedLiElement = document.querySelector(`[data-message-id="${messageId}"]`) as HTMLElement;
            if (loadedLiElement) {
              const messageElement = loadedLiElement.querySelector('.str-chat__message') as HTMLElement;
              if (messageElement) {
                loadedLiElement.scrollIntoView({ 
                  behavior: 'smooth', 
                  block: 'center' 
                });
                
                setTimeout(() => {
                  flashMessage(messageElement);
                }, 300);
              }
            }
            setIsLoading(false);
          }, 1000); // Give time for Stream to load and render
        } else {
          // Fallback: manually load message history around that message
          const response = await channel.query({
            messages: { limit: 50, id_around: messageId }
          });
          
          if (response.messages) {
            // Wait for React to render the new messages
            setTimeout(() => {
              const loadedLiElement = document.querySelector(`[data-message-id="${messageId}"]`) as HTMLElement;
              if (loadedLiElement) {
                const messageElement = loadedLiElement.querySelector('.str-chat__message') as HTMLElement;
                if (messageElement) {
                  loadedLiElement.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center' 
                  });
                  
                  setTimeout(() => {
                    flashMessage(messageElement);
                  }, 300);
                }
              } else {
                alert('Could not find the quoted message. It may have been deleted.');
              }
              setIsLoading(false);
            }, 1000);
          }
        }
      } catch (error) {
        ErrorLogger.log(error, 'QuotedMessageDisplay.handleClick');
        alert('Could not load the quoted message. It may have been deleted or you may not have permission to view it.');
        setIsLoading(false);
      }
    }
  }, [quotedMessage, channel, jumpToMessage, flashMessage, isLoading]);

  if (!quotedMessage) {
    return null;
  }

  const senderName = quotedMessage.user?.name || quotedMessage.user?.id || 'Unknown';
  const messageText = quotedMessage.text || '';
  
  // Truncate long messages
  const truncatedText = messageText.length > 80 
    ? messageText.substring(0, 80) + '...' 
    : messageText;

  return (
    <div className={`quoted-message-display ${isLoading ? 'quoted-message-display--loading' : ''}`} onClick={handleClick}>
      <div className="quoted-message-display__border"></div>
      <div className="quoted-message-display__content">
        <div className="quoted-message-display__sender">{senderName}</div>
        <div className="quoted-message-display__text">
          {isLoading ? 'Loading message...' : (truncatedText || 'Attachment')}
        </div>
      </div>
      {isLoading && (
        <div className="quoted-message-display__spinner">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};

